// PWA install plumbing. The browser's beforeinstallprompt event can fire
// once, early (e.g. while the user is still on the Register screen), and
// if we don't capture it then, it's gone. So we attach the listener at
// module load (imported from main.tsx before render) and stash the event
// for whoever wants it later (the Home banner, the Settings button).

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

let deferred: BeforeInstallPromptEvent | null = null;
let installed = false;
const listeners = new Set<() => void>();

function notify() {
  for (const l of listeners) l();
}

/** Call once, as early as possible (main.tsx). Idempotent. */
let inited = false;
export function initPwa(): void {
  if (inited || typeof window === 'undefined') return;
  inited = true;

  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferred = e as BeforeInstallPromptEvent;
    notify();
  });

  window.addEventListener('appinstalled', () => {
    deferred = null;
    installed = true;
    notify();
  });

  // Register the service worker (required for installability on Chrome).
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js').catch(() => undefined);
    });
  }

  // Detect whether the app is already installed — even when viewed in a
  // browser tab (where display-mode isn't standalone). Chrome exposes
  // getInstalledRelatedApps(), which returns our PWA and/or our Play app
  // when they're installed (thanks to the related_applications in the
  // manifest). This is what stops the "install" nag from showing on a
  // device that already has the app.
  void detectInstalled();
}

async function detectInstalled(): Promise<void> {
  if (installed) return;
  if (isStandalone()) {
    installed = true;
    notify();
    return;
  }
  const nav = navigator as Navigator & {
    getInstalledRelatedApps?: () => Promise<Array<{ platform?: string; id?: string; url?: string }>>;
  };
  if (typeof nav.getInstalledRelatedApps === 'function') {
    try {
      const apps = await nav.getInstalledRelatedApps();
      if (Array.isArray(apps) && apps.length > 0) {
        installed = true;
        notify();
      }
    } catch {
      // unsupported / blocked — leave installed as-is
    }
  }
}

/**
 * True if the app is installed on this device — either running standalone,
 * or detected via getInstalledRelatedApps while in a browser tab, or we saw
 * the appinstalled event this session.
 */
export function isInstalled(): boolean {
  return installed || isStandalone();
}

export type Platform = 'ios' | 'android' | 'desktop';

export function platform(): Platform {
  if (typeof navigator === 'undefined') return 'desktop';
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && 'ontouchend' in document)) {
    return 'ios';
  }
  if (/Android/.test(ua)) return 'android';
  return 'desktop';
}

/** Manual "add to home screen" steps for the current platform. */
export function installInstructions(): string {
  switch (platform()) {
    case 'ios':
      return 'In Safari, tap the Share button (the square with an arrow) at the bottom, then choose “Add to Home Screen.”';
    case 'android':
      return 'Tap the ⋮ menu at the top-right of Chrome, then “Install app” (or “Add to Home screen”).';
    default:
      return 'Click the install icon in the address bar, or open the ⋮ menu and choose “Install No Rush.”';
  }
}

export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const mm =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(display-mode: standalone)').matches;
  return mm || Boolean((navigator as { standalone?: boolean }).standalone);
}

export function canInstall(): boolean {
  return deferred !== null;
}

export function wasInstalled(): boolean {
  return installed;
}

/** Subscribe to availability changes (prompt arrived, or app installed). */
export function subscribeInstall(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export type InstallOutcome = 'accepted' | 'dismissed' | 'unavailable';

export async function promptInstall(): Promise<InstallOutcome> {
  if (!deferred) return 'unavailable';
  // A beforeinstallprompt is single-use: prompt() can only be called once,
  // so consume our reference immediately (whether the user accepts or
  // cancels). Chrome will fire a fresh event on a later visit if the app
  // is still installable and not installed.
  const event = deferred;
  deferred = null;
  notify();
  try {
    await event.prompt();
    const choice = await event.userChoice;
    return choice.outcome;
  } catch {
    return 'unavailable';
  }
}
